import React from 'react';
import { ScrollArea } from '@cotera/client/v0/components/ui/scroll-area';
import { Skeleton } from '@cotera/client/v0/components/ui/skeleton';
import { cn } from '@cotera/client/v0/lib/utils';
import { RESOURCE_LIST_SKELETON_CLASS } from './resource-list-view-constants';

/**
 * Card-shaped placeholders for grid mode.
 *
 * The list skeleton below is a `<table>`, so rendering it while the grid is
 * selected showed table rows that then popped into cards — the loading state
 * has to predict the layout it's standing in for.
 */
export function ResourceGridLoadingSkeleton({
  columns,
  cardHeight,
  gap,
}: {
  columns: number;
  cardHeight: number;
  gap: number;
}): React.ReactElement {
  const placeholderCount = Math.max(columns, 1) * 2;
  return (
    <ScrollArea className="flex-1 min-h-0 -mr-4 pr-4">
      <div
        className="w-full pt-2"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.max(
            columns,
            1
          )}, minmax(0, 1fr))`,
          gap: `${gap}px`,
        }}
      >
        {Array.from({ length: placeholderCount }, (_, i) => (
          <div
            key={i}
            style={{ height: `${cardHeight}px` }}
            className="flex flex-col rounded-lg border border-border/60 bg-card p-3"
          >
            <div className="flex items-center gap-2.5">
              <Skeleton
                className={cn(
                  RESOURCE_LIST_SKELETON_CLASS,
                  'h-8 w-8 shrink-0 rounded-md'
                )}
              />
              <Skeleton
                className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-4 rounded')}
                style={{ width: `${40 + ((i * 13) % 35)}%` }}
              />
            </div>
            <div className="mt-3 flex flex-1 flex-col gap-1.5">
              <Skeleton
                className={cn(
                  RESOURCE_LIST_SKELETON_CLASS,
                  'h-3 w-full rounded'
                )}
              />
              <Skeleton
                className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-3 rounded')}
                style={{ width: `${50 + ((i * 11) % 30)}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-border/50 pt-2">
              <Skeleton
                className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-3 w-16 rounded')}
              />
              <Skeleton
                className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-3 w-8 rounded')}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="pb-8" />
    </ScrollArea>
  );
}

export function ResourceListLoadingSkeleton({
  colgroup,
  columnCount,
}: {
  colgroup: React.ReactNode;
  columnCount: number;
}): React.ReactElement {
  return (
    <ScrollArea className="flex-1 min-h-0 -mr-4 pr-4">
      <table className="w-full table-fixed border-separate border-spacing-0">
        {colgroup}
        <tbody>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <tr key={i}>
              <td
                className="py-3 px-3 border-b border-border/40"
                colSpan={columnCount + 1}
              >
                <div className="flex items-center gap-3">
                  <Skeleton
                    className={cn(
                      RESOURCE_LIST_SKELETON_CLASS,
                      'h-4 w-4 rounded shrink-0'
                    )}
                  />
                  <Skeleton
                    className={cn(RESOURCE_LIST_SKELETON_CLASS, 'h-4 rounded')}
                    style={{ width: `${30 + ((i * 17) % 40)}%` }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pb-8" />
    </ScrollArea>
  );
}
